/**
 * What the search box offers and what it accepts — the matching half of `components/Search.tsx`,
 * kept out of the component so it can be tested without a DOM (`test/search.test.ts`).
 *
 * The box is a two-stage combobox: {@link suggest} answers the axes while none is chosen, and that
 * axis's values once one is. {@link parseTerm} is the other way in — a free-typed or pasted
 * `axis:value` — and it RETURNS its refusals rather than raising a toast, because a pure function
 * that decides and a component that tells the user are easier to keep honest than one that does
 * both (and `api.ts` reaches for `localStorage`, which there is none of in a test).
 */
import { FILTER_AXES, MAX_TERMS } from '../shared/queries.ts';
import { TAGS } from '../shared/seed.ts';
import type { FilterTerm, IssueFilter, SearchAxis } from '../shared/queries.ts';
import { ISSUE_PRIORITIES, ISSUE_STATUSES } from '../shared/schema.ts';
import { PRIORITY_LABELS, STATUS_LABELS } from './format.ts';
import type { User } from './types.ts';

/** A row of the dropdown: an axis to narrow to, or a value to commit. */
export type Option =
  | { kind: 'axis'; axis: SearchAxis; label: string; hint: string }
  | { kind: 'value'; axis: SearchAxis; value: string; label: string; hint: string };

/** What each axis asks for, in the placeholder, once it is the one being filled. */
export const PROMPT: Record<SearchAxis, string> = {
  title: 'words in the title…', status: 'which status…', priority: 'which priority…',
  owner: 'whose issues…', tag: 'which tag…', comment: 'words in a comment…',
};
const AXIS_HINT: Record<SearchAxis, string> = {
  title: 'words in the title', status: 'one of five', priority: 'one of four',
  owner: 'whose issue it is', tag: 'a label on the issue', comment: 'words in a comment',
};
export const SUGGEST_LIMIT = 8;
const MAX_TERM_LENGTH = 60;

/** A tag as `addTag` writes it, which is what makes an exact comparison the right one. */
export const normalizeTag = (value: string) =>
  value.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 28);

/**
 * The rows under the box: the axes, or — once one is chosen — its values.
 *
 * Stage one puts the axes whose name STARTS with what was typed ahead of the ones that merely
 * contain it, then offers the typed text as a title search, which is both the old bare-text
 * behaviour and the reason the list is never empty while there is something to commit.
 *
 * Stage two is the vocabulary of that axis, and it is what makes `owner:` and `tag:` exact: the
 * list sends an id, not the characters that found it. `title:` and `comment:` have no vocabulary —
 * a substring is the question — so their one row is the text itself.
 */
export function suggest(axis: SearchAxis | null, draft: string, users: User[]): Option[] {
  const needle = draft.trim().toLowerCase();
  const typed = draft.trim().slice(0, MAX_TERM_LENGTH);
  if (!axis) {
    const matches = FILTER_AXES.filter(a => a.includes(needle));
    const axes: Option[] = [...matches.filter(a => a.startsWith(needle)), ...matches.filter(a => !a.startsWith(needle))]
      .map(a => ({ kind: 'axis', axis: a, label: `${a}:`, hint: AXIS_HINT[a] }));
    const free: Option[] = typed ? [{ kind: 'value', axis: 'title', value: typed, label: `contains “${typed}”`, hint: 'title' }] : [];
    return [...axes, ...free].slice(0, SUGGEST_LIMIT);
  }
  const matching = <T,>(items: readonly T[], text: (item: T) => string[]) =>
    items.filter(item => !needle || text(item).some(t => t.toLowerCase().includes(needle))).slice(0, SUGGEST_LIMIT);
  switch (axis) {
    case 'status':
      return matching(ISSUE_STATUSES, s => [s, STATUS_LABELS[s]]).map(s => ({ kind: 'value', axis, value: s, label: STATUS_LABELS[s], hint: s }));
    case 'priority':
      return matching(ISSUE_PRIORITIES, p => [p, PRIORITY_LABELS[p]]).map(p => ({ kind: 'value', axis, value: p, label: PRIORITY_LABELS[p], hint: p }));
    case 'owner':
      // The id is what the term carries and what the query compares, so it is shown beside the
      // name rather than hidden behind it.
      return matching(users, u => [u.id, u.name]).map(u => ({ kind: 'value', axis, value: u.id, label: u.name, hint: u.id }));
    case 'tag': {
      const known: Option[] = matching(TAGS, t => [t]).map(t => ({ kind: 'value', axis, value: t, label: t, hint: 'tag' }));
      // A visitor can invent a tag, so an unknown one has to be offerable — otherwise the box can
      // find every tag except the ones made on this board today. Only when nothing known matches,
      // though: typing toward `sync` would otherwise grow a phantom `syn` row at every keystroke.
      const invented = normalizeTag(draft);
      if (known.length || !invented) return known;
      const offer: Option = { kind: 'value', axis, value: invented, label: invented, hint: 'new tag' };
      return [...known, offer].slice(0, SUGGEST_LIMIT);
    }
    case 'title':
    case 'comment':
      return typed ? [{ kind: 'value', axis, value: typed, label: `contains “${typed}”`, hint: axis }] : [];
  }
}

/** A term, or why there is none: `null` for nothing typed, a sentence for something refused. */
export type Parsed = { term: FilterTerm } | { refusal: string } | null;

/**
 * Text to a term, for what the list cannot resolve: a free-typed value in stage two, or a pasted
 * `axis:value`. Every refusal here is one the list would not have let you make.
 */
export function parseTerm(raw: string, users: User[]): Parsed {
  const text = raw.trim();
  if (!text) return null;
  const colon = text.indexOf(':');
  const axis = colon > 0 ? text.slice(0, colon).trim().toLowerCase() : '';
  if (!FILTER_AXES.includes(axis as SearchAxis)) return { term: { axis: 'title', value: text.slice(0, MAX_TERM_LENGTH) } };
  const value = text.slice(colon + 1).trim().slice(0, MAX_TERM_LENGTH);
  if (!value) return null;
  if (axis === 'status') {
    const status = ISSUE_STATUSES.find(s => s === value.toLowerCase() || STATUS_LABELS[s].toLowerCase() === value.toLowerCase());
    return status ? { term: { axis, value: status } } : { refusal: `Status is one of: ${ISSUE_STATUSES.join(', ')}.` };
  }
  if (axis === 'priority') {
    const priority = ISSUE_PRIORITIES.find(p => p === value.toLowerCase());
    return priority ? { term: { axis, value: priority } } : { refusal: `Priority is one of: ${ISSUE_PRIORITIES.join(', ')}.` };
  }
  if (axis === 'owner') {
    // By id, or by the name shown in the picker. Refused rather than passed through as a substring:
    // a typo would otherwise become an `ILIKE` over every candidate row and silently match nothing,
    // which looks identical to "that person owns no issues here".
    const needle = value.toLowerCase();
    const owner = users.find(u => u.id.toLowerCase() === needle)
      ?? users.find(u => u.name.toLowerCase() === needle)
      ?? users.find(u => u.name.toLowerCase().startsWith(needle));
    return owner ? { term: { axis, value: owner.id } } : { refusal: `No user matches "${value}". Pick one from the list.` };
  }
  if (axis === 'tag') {
    // Normalized to the shape `addTag` writes, so an exact comparison is the right one. Not
    // refused against `TAGS`, because a visitor can invent a tag and must be able to find it.
    const tag = normalizeTag(value);
    return tag ? { term: { axis, value: tag } } : { refusal: 'A tag is letters, digits and hyphens.' };
  }
  return { term: { axis: axis as SearchAxis, value } };
}

/**
 * A term added to a filter. One rule, in one place, because the box is no longer the only way to
 * make a term: a tag clicked in a row or in the detail pane adds the one the `tag:` list would
 * have. The same term twice is the filter unchanged, and a seventh is `null` — left for the
 * caller, which is the half of the app that can tell the user why.
 */
export function withTerm(filter: IssueFilter, term: FilterTerm): IssueFilter | null {
  if (filter.some(t => t.axis === term.axis && t.value === term.value)) return filter;
  if (filter.length >= MAX_TERMS) return null;
  return [...filter, term];
}

/** What a committed pill reads: the label for the exact axes, and the owner's NAME for the id the
 *  term carries. */
export function termLabel(term: FilterTerm, users: User[]): string {
  if (term.axis === 'status') return STATUS_LABELS[term.value as keyof typeof STATUS_LABELS] ?? term.value;
  if (term.axis === 'priority') return PRIORITY_LABELS[term.value as keyof typeof PRIORITY_LABELS] ?? term.value;
  if (term.axis === 'owner') return users.find(u => u.id === term.value)?.name ?? term.value;
  return term.value;
}
